// Projeto: Haklai Church — Plugin WordPress Modular - Desenvolvido por: Ricardo Sarmento - https://linx.pt

import React, { useState, useEffect } from 'react';
import api from '../services/api';
import AppLayout from '../components/AppLayout';
import LoadingSpinner from '../components/LoadingSpinner';

function ImportPage() {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [status, setStatus] = useState(null); // { type: 'success' | 'error', message: string, details?: any }
  
  const [cells, setCells] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loadingData, setLoadingData] = useState(true);

  useEffect(() => {
    // Fetch cells and roles for the glossaries
    const fetchData = async () => {
      setLoadingData(true);
      try {
        const [cellsRes, rolesRes] = await Promise.all([
          api.get('/cells'),
          api.get('/roles')
        ]);
        setCells(cellsRes.data.data.items || []);
        setRoles(rolesRes.data.data || []);
      } catch (err) {
        console.error('Erro ao buscar dados para o glossário', err);
      } finally {
        setLoadingData(false);
      }
    };
    fetchData();
  }, []);

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      setFile(e.target.files[0]);
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!file) {
      setStatus({ type: 'error', message: 'Selecione um ficheiro CSV primeiro.' });
      return;
    }

    setUploading(true);
    setStatus(null);

    const formData = new FormData();
    formData.append('file', file);
    
    // Configurações de importação
    formData.append('options', JSON.stringify({
      on_duplicate: 'skip',
      dry_run: false
    }));

    try {
      const response = await api.post('/import', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      if (response.data.success) {
        setStatus({ 
          type: 'success', 
          message: 'Ficheiro recebido com sucesso! A importação está a ser processada em background. Verifique os Membros após alguns instantes.',
          details: response.data.data
        });
        setFile(null);
        // Reset file input
        const fileInput = document.getElementById('csv_file');
        if (fileInput) fileInput.value = '';
      }
    } catch (err) {
      console.error('Erro na importação:', err);
      const msg = err.response?.data?.message || 'Erro ao importar ficheiro.';
      setStatus({ type: 'error', message: msg });
    } finally {
      setUploading(false);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const response = await api.get('/import/template', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'haklai-import-template.csv');
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
    } catch (err) {
      console.error('Erro ao baixar template:', err);
    }
  };

  return (
    <AppLayout>
      <div style={{ padding: '2rem', maxWidth: '1000px', margin: '0 auto' }}>
        <div style={{ marginBottom: '2rem' }}>
          <h1 style={{ fontSize: '1.875rem', fontWeight: 'bold', color: 'var(--text-primary)' }}>
            Importar Membros
          </h1>
          <p style={{ color: 'var(--text-muted)', marginTop: '0.25rem' }}>
            Faça upload de um ficheiro CSV para adicionar membros em massa.
          </p>
        </div>

        {status && (
          <div className={`alert alert-${status.type}`} style={{ marginBottom: '1.5rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <span className="material-symbols-outlined">
                {status.type === 'success' ? 'check_circle' : 'error'}
              </span>
              <span>{status.message}</span>
            </div>
            {status.details?.validation?.errors?.length > 0 && (
              <ul style={{ marginTop: '0.5rem', marginLeft: '2rem', color: 'var(--danger)', fontSize: '0.875rem' }}>
                {status.details.validation.errors.map((err, idx) => (
                  <li key={idx}>{err}</li>
                ))}
              </ul>
            )}
          </div>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2rem' }}>
          {/* Lado Esquerdo: Upload */}
          <div className="card" style={{ padding: '2rem', borderRadius: '12px', backgroundColor: 'var(--surface)', border: '1px solid var(--border)' }}>
            <h2 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <span className="material-symbols-outlined" style={{ color: 'var(--primary)' }}>upload_file</span>
              Ficheiro CSV
            </h2>

            <p style={{ fontSize: '0.875rem', color: 'var(--text-muted)', marginBottom: '1.5rem' }}>
              Faça o download do nosso template, preencha os dados e faça o upload abaixo.
              Assegure-se de usar os IDs corretos de Célula e Função (ver tabelas ao lado).
            </p>

            <button 
              type="button" 
              onClick={handleDownloadTemplate}
              className="btn btn-outline"
              style={{ marginBottom: '1.5rem', width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '0.5rem' }}
            >
              <span className="material-symbols-outlined">download</span>
              Baixar Template CSV
            </button>

            <form onSubmit={handleUpload}>
              <div className="form-group" style={{ marginBottom: '1.5rem' }}>
                <label className="form-label" style={{ fontWeight: '500', marginBottom: '0.5rem', display: 'block' }}>Selecionar Ficheiro</label>
                <input 
                  type="file" 
                  id="csv_file"
                  accept=".csv"
                  onChange={handleFileChange}
                  className="form-input"
                  style={{ width: '100%', padding: '0.5rem', border: '1px dashed var(--border)', borderRadius: '8px' }}
                />
              </div>

              <button 
                type="submit" 
                className="btn btn-primary"
                disabled={!file || uploading}
                style={{ width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '0.5rem' }}
              >
                {uploading ? (
                  <>A importar...</>
                ) : (
                  <>
                    <span className="material-symbols-outlined">cloud_upload</span>
                    Iniciar Importação
                  </>
                )}
              </button>
            </form>
          </div>

          {/* Lado Direito: Glossários */}
          <div style={{ display: 'flex', flexDirection: 'col', gap: '1.5rem' }}>
            <div className="card" style={{ padding: '2rem', borderRadius: '12px', backgroundColor: 'var(--surface)', border: '1px solid var(--border)', flex: 1 }}>
              <h2 style={{ fontSize: '1.1rem', fontWeight: '600', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <span className="material-symbols-outlined" style={{ color: 'var(--primary)', fontSize: '1.25rem' }}>groups</span>
                Glossário: ID da Célula
              </h2>
              {loadingData ? (
                <LoadingSpinner size="sm" />
              ) : (
                <div style={{ maxHeight: '200px', overflowY: 'auto', border: '1px solid var(--border)', borderRadius: '8px' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
                    <thead style={{ backgroundColor: 'var(--background)' }}>
                      <tr>
                        <th style={{ padding: '0.5rem 1rem', textAlign: 'left', borderBottom: '1px solid var(--border)', width: '60px' }}>ID</th>
                        <th style={{ padding: '0.5rem 1rem', textAlign: 'left', borderBottom: '1px solid var(--border)' }}>Nome da Célula</th>
                      </tr>
                    </thead>
                    <tbody>
                      {cells.length > 0 ? cells.map(cell => (
                        <tr key={cell.id} style={{ borderBottom: '1px solid var(--border)' }}>
                          <td style={{ padding: '0.5rem 1rem', fontWeight: '600', color: 'var(--primary)' }}>{cell.id}</td>
                          <td style={{ padding: '0.5rem 1rem' }}>{cell.name}</td>
                        </tr>
                      )) : (
                        <tr>
                          <td colSpan="2" style={{ padding: '1rem', textAlign: 'center', color: 'var(--text-muted)' }}>Sem células registadas.</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            <div className="card" style={{ padding: '2rem', borderRadius: '12px', backgroundColor: 'var(--surface)', border: '1px solid var(--border)', flex: 1 }}>
              <h2 style={{ fontSize: '1.1rem', fontWeight: '600', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <span className="material-symbols-outlined" style={{ color: 'var(--primary)', fontSize: '1.25rem' }}>badge</span>
                Glossário: ID da Função
              </h2>
              {loadingData ? (
                <LoadingSpinner size="sm" />
              ) : (
                <div style={{ maxHeight: '200px', overflowY: 'auto', border: '1px solid var(--border)', borderRadius: '8px' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
                    <thead style={{ backgroundColor: 'var(--background)' }}>
                      <tr>
                        <th style={{ padding: '0.5rem 1rem', textAlign: 'left', borderBottom: '1px solid var(--border)', width: '60px' }}>ID</th>
                        <th style={{ padding: '0.5rem 1rem', textAlign: 'left', borderBottom: '1px solid var(--border)' }}>Nome da Função</th>
                      </tr>
                    </thead>
                    <tbody>
                      {roles.length > 0 ? roles.map(role => (
                        <tr key={role.id} style={{ borderBottom: '1px solid var(--border)' }}>
                          <td style={{ padding: '0.5rem 1rem', fontWeight: '600', color: 'var(--primary)' }}>{role.id}</td>
                          <td style={{ padding: '0.5rem 1rem' }}>{role.name}</td>
                        </tr>
                      )) : (
                        <tr>
                          <td colSpan="2" style={{ padding: '1rem', textAlign: 'center', color: 'var(--text-muted)' }}>Sem funções registadas.</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>

        </div>
      </div>
    </AppLayout>
  );
}

export default ImportPage;
